import { useEffect, useState, useCallback, useRef } from "react";
import { apiBaseUrl } from "../../utils/constants";

export interface ICoordinates {
  lat: number;
  lon: number;
}
export interface IUserLocationInfo {
  lat: number | null;
  lon: number | null;
  zip: string;
  [key: string]: unknown;
}

interface IUseUserLocationInfoOptions {
  fallbackZip?: string;
  fetchOnMount?: boolean;
}

interface IUseUserLocationInfo {
  zipCode: string;
  data: IUserLocationInfo | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => void;
}
interface ICachedLocationData {
  data: IUserLocationInfo;
  timestamp: number;
  expiresAt: number;
}

const LOCATION_CACHE_KEY = "har_user_location_cache";
const CACHE_EXPIRY_TIME = 1 * 24 * 60 * 60 * 1000; // 1 day

/**
 *   undefined  → not attempted yet (initial / after refresh)
 *   ICoordinates → GPS acquired successfully
 *   null       → GPS denied / unavailable; fall back to IP-based lookup
 */
type CoordinatesState = ICoordinates | null | undefined;

const setLocationCache = (data: IUserLocationInfo) => {
  try {
    if (typeof Storage === "undefined") {
      console.warn("[useUserLocationInfo] localStorage not supported");
      return;
    }
    const timestamp = Date.now();
    const cacheData: ICachedLocationData = {
      data,
      timestamp,
      expiresAt: timestamp + CACHE_EXPIRY_TIME,
    };
    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn("[useUserLocationInfo] Failed to cache location data:", error);
    try {
      localStorage.removeItem(LOCATION_CACHE_KEY);
    } catch (clearError) {
      console.warn("[useUserLocationInfo] Failed to clear cache:", clearError);
    }
  }
};

const getLocationCache = (): IUserLocationInfo | null => {
  try {
    if (typeof Storage === "undefined") return null;

    const cached = localStorage.getItem(LOCATION_CACHE_KEY);
    if (!cached) return null;

    const parsedCache: ICachedLocationData = JSON.parse(cached);
    const now = Date.now();

    if (
      !parsedCache.data ||
      !parsedCache.expiresAt ||
      typeof parsedCache.expiresAt !== "number"
    ) {
      console.warn(
        "[useUserLocationInfo] Invalid cache structure, removing...",
      );
      localStorage.removeItem(LOCATION_CACHE_KEY);
      return null;
    }

    if (now > parsedCache.expiresAt) {
      console.warn("[useUserLocationInfo] Location cache expired, removing...");
      localStorage.removeItem(LOCATION_CACHE_KEY);
      return null;
    }

    return parsedCache.data;
  } catch (error) {
    console.warn("[useUserLocationInfo] Failed to read location cache:", error);
    try {
      localStorage.removeItem(LOCATION_CACHE_KEY);
    } catch (removeError) {
      console.warn(
        "[useUserLocationInfo] Failed to remove corrupted cache:",
        removeError,
      );
    }
    return null;
  }
};

const clearLocationCache = (): void => {
  try {
    if (typeof Storage !== "undefined") {
      localStorage.removeItem(LOCATION_CACHE_KEY);
    }
  } catch (e) {
    console.warn("[useUserLocationInfo] Failed to clear cache:", e);
  }
};

export function useUserLocationInfo(
  options: IUseUserLocationInfoOptions = {},
): IUseUserLocationInfo {
  const { fallbackZip = "77027", fetchOnMount = true } = options;
  const [data, setData] = useState<IUserLocationInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(fetchOnMount);
  const [error, setError] = useState<Error | null>(null);
  const [coordinates, setCoordinates] = useState<CoordinatesState>(undefined);
  const mounted = useRef(true);
  const prevFetchOnMount = useRef(fetchOnMount);
  const forceFetch = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const safeSet = useCallback(<T>(setter: (v: T) => void, v: T) => {
    if (mounted.current) setter(v);
  }, []);

  useEffect(() => {
    if (coordinates !== undefined || (!fetchOnMount && !forceFetch.current))
      return;

    forceFetch.current = false;

    // Check cache first — skip geolocation entirely if valid cache exists.
    const cached = getLocationCache();

    if (cached) {
      safeSet(setData, cached);
      safeSet(setError, null);
      return;
    }

    if (!("geolocation" in navigator)) {
      console.warn(
        "[useUserLocationInfo] Geolocation not supported in this context.",
      );
      safeSet(setCoordinates, null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lon } = position.coords;
        safeSet(setCoordinates, { lat, lon });
      },
      (geoErr) => {
        const msg =
          (
            {
              1: "Permission denied by the user or blocked by parent frame.",
              2: "Position unavailable.",
              3: "Request timed out.",
            } as Record<number, string>
          )[geoErr?.code] ??
          geoErr?.message ??
          "Geolocation error";
        console.warn("[useUserLocationInfo] Geolocation error:", msg);
        safeSet(setCoordinates, null);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [coordinates, fetchOnMount, safeSet]);

  // Triggered once `coordinates` has a resolved value (ICoordinates | null).
  useEffect(() => {
    // Wait until geolocation attempt is complete.
    if (coordinates === undefined) return;

    const controller = new AbortController();

    (async () => {
      safeSet(setIsLoading, true);

      let url = `${apiBaseUrl}/api/v1/chatbot/user/location`;
      if (coordinates !== null) {
        url += `?lat=${coordinates.lat}&lon=${coordinates.lon}`;
      }

      try {
        const res = await fetch(url, {
          signal: controller.signal,
          credentials: "include",
        });

        if (!res.ok) {
          console.warn(
            `[useUserLocationInfo] Location API non-OK (${res.status}). Using fallback zip.`,
          );
          safeSet(setError, null);
          return;
        }

        const json: any = await res.json();

        if (!json?.status) {
          console.warn(
            "[useUserLocationInfo] API returned error payload; using fallback zip.",
          );
          safeSet(setError, null);
          return;
        }

        const { gps = [], zip: apiZip, ...rest } = json.data ?? {};
        const [gpsLat, gpsLon] = gps as [number?, number?];
        const lat: number | null = gpsLat ?? coordinates?.lat ?? null;
        const lon: number | null = gpsLon ?? coordinates?.lon ?? null;

        const zip = apiZip || fallbackZip;

        const locationData: IUserLocationInfo = { lat, lon, zip, ...rest };

        // Cache only after we have the real zip from the API.
        if (lat != null && lon != null && apiZip) {
          setLocationCache(locationData);
        }
        safeSet(setData, locationData);
        safeSet(setError, null);
      } catch (e: unknown) {
        if ((e as { name?: string })?.name === "AbortError") return;
        console.error("[useUserLocationInfo] Fetch failed:", e);
        safeSet(setError, e as Error);
      } finally {
        safeSet(setIsLoading, false);
      }
    })();

    return () => controller.abort();
  }, [coordinates, fallbackZip, safeSet]);

  useEffect(() => {
    const wasEnabled = prevFetchOnMount.current;
    prevFetchOnMount.current = fetchOnMount;
    if (!wasEnabled && fetchOnMount && coordinates !== undefined) {
      clearLocationCache();
      safeSet(setCoordinates, undefined);
    }
  }, [fetchOnMount, coordinates, safeSet]);

  const refresh = useCallback(() => {
    clearLocationCache();
    forceFetch.current = true;
    safeSet(setData, null);
    safeSet(setError, null);
    safeSet(setCoordinates, undefined);
  }, [safeSet]);

  return {
    zipCode: data?.zip ?? fallbackZip,
    data,
    isLoading,
    error,
    refresh,
  };
}
